import React, { useState, useEffect } from "react";
import { Box, IconButton, MenuItem, TextField } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { type GridCellEditCommitParams } from "@mui/x-data-grid-pro";

interface SelectOption {
  value: string;
  label: string;
}

export interface EditableSelectCellProps {
  value: string;
  displayValue?: string;
  rowId: number;
  field: string;
  options: SelectOption[];
  onEdit: (params: GridCellEditCommitParams) => void;
  disabled?: boolean;
  onEditingChange?: (rowId: number, field: string, isEditing: boolean) => void;
}

/**
 * 編集可能なセレクトセルコンポーネント
 * ホバー時に編集アイコンを表示し、クリックで編集モードに切り替わる
 */
export const EditableSelectCell: React.FC<EditableSelectCellProps> = ({
  value,
  displayValue,
  rowId,
  field,
  options,
  onEdit,
  disabled = false,
  onEditingChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isHovered, setIsHovered] = useState(false);

  // valueプロップが変更されたらeditValueを更新
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value);
    }
  }, [value, isEditing]);

  const displayText = displayValue !== undefined ? displayValue : value;

  /**
   * 編集をキャンセルする
   */
  const handleCancel = (): void => {
    setEditValue(value);
    setIsEditing(false);
    onEditingChange?.(rowId, field, false);
  };

  /**
   * 編集モード開始
   */
  const handleStartEdit = (): void => {
    setIsEditing(true);
    onEditingChange?.(rowId, field, true);
  };

  if (isEditing) {
    return (
      <Box sx={{ width: "100%", p: 1 }}>
        <TextField
          select
          value={editValue}
          onChange={(e) => {
            const newValue = e.target.value;
            setEditValue(newValue);
            // 選択したら即座に保存
            setIsEditing(false);
            onEditingChange?.(rowId, field, false);
            if (newValue !== value) {
              const params: GridCellEditCommitParams = {
                id: rowId,
                field,
                value: newValue,
              };
              onEdit(params);
            }
          }}
          size="small"
          autoFocus
          fullWidth
          SelectProps={{
            // ドロップダウンを自動で開く
            open: true,
            onClose: () => {
              // ドロップダウンを閉じたら編集モードも終了（キャンセル扱い）
              handleCancel();
            },
          }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    );
  }

  if (disabled) {
    return (
      <Box sx={{ textAlign: "left", width: "100%", py: 1 }}>{displayText}</Box>
    );
  }

  return (
    <Box
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        width: "100%",
        height: "100%",
        minHeight: "40px",
        py: 1,
      }}
    >
      <Box sx={{ flexGrow: 1 }}>{displayText}</Box>
      {isHovered && (
        <IconButton size="small" onClick={handleStartEdit}>
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};
